import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classNames from "classnames";
import { URLS } from "@constants";
import PaymentActions from "@redux/payments";
import NotificationsModal from "../NotificationsModal/NotificationsModal";
import ImpersonateModal from "../ImpersonateModal/ImpersonateModal";
import Image from "../Image/Image";

import TextLogo from "../../images/logo-text.svg";
import IconLogo from "../../images/logo-icon.svg";
import "./Navbar.scss";

class Navbar extends Component {
	constructor(props) {
		super(props);
		this.state = {
			mainMenu: [],
			dropDown: [],
			isMobileDropdownOpen: false,
			impersonating: false,
		};
	}

	componentDidMount() {
		const { user } = this.props;
		this.setState({ mainMenu: user.getMainMenu(), dropDown: user.getUserMenu() });
		document.addEventListener("click", this.clickEventListener);
	}

	componentWillUnmount() {
		document.removeEventListener("click", this.clickEventListener);
	}

	/**
	 * Listens for clicks so that the user menu can be closed automatically if the user clicks outside of the menu
	 * @param {Event} event The HTML event handled by this method
	 */
	clickEventListener = ({ target }) => {
		const dropdown = document.getElementById("user-menu");
		if (dropdown.classList.contains("open") && !target.closest("#user-menu")) {
			dropdown.classList.remove("open");
		}
	};

	toggleNavCollapse = () => {
		const links = document.getElementById("navbar-links");
		if (links.classList.contains("in")) links.classList.remove("in");
		else links.classList.add("in");
	};

	toggleDropdown = () => {
		const dropdown = document.getElementById("user-menu");
		if (dropdown.classList.contains("open")) dropdown.classList.remove("open");
		else dropdown.classList.add("open");
	};

	toggleMobileDropdown = () => {
		this.setState(prevState => ({ isMobileDropdownOpen: !prevState.isMobileDropdownOpen }));
	};

	openNotificationsModal = () => {
		this._notificationsModal.openModal();
	};

	toggleImpersonation = () =>
		this.setState(prevState => ({ impersonating: !prevState.impersonating }));

	handleLinkClick = url => {
		const { resetPaymentFilters } = this.props;
		// Close the dropdown menu (desktop) or collapse the navbar and mobile dropdown (mobile)
		document.getElementById("navbar-links").classList.remove("in");
		document.getElementById("user-menu").classList.remove("open");
		this.setState({ isMobileDropdownOpen: false });

		switch (url) {
			case "/payments":
				resetPaymentFilters();
				break;
			case "impersonate":
				this.toggleImpersonation();
				break;
			default:
				break;
		}
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const { mainMenu, dropDown, isMobileDropdownOpen, impersonating } = this.state;
		const {
			location: { pathname },
		} = window;
		return (
			<nav
				className="navbar navbar-default navbar-static-top"
				id="app-navbar"
			>
				<div className="container-fluid">
					<div className="row">
						{/* Far-left (Brand) */}
						<div className="col-xs-12 col-sm-2 navbar-header">
							<button
								type="button"
								className="navbar-toggle collapsed"
								data-toggle="collapse"
								data-target="navbar-links"
								aria-expanded="false"
								onClick={() => this.toggleNavCollapse()}
							>
								<span className="sr-only">Toggle navigation</span>
								<span className="icon-bar" />
								<span className="icon-bar" />
								<span className="icon-bar" />
							</button>
							<Link
								className="navbar-brand"
								to="/"
								onClick={() => this.handleLinkClick()}
							>
								<img
									src={IconLogo}
									className="visible-xs visible-sm brand-img"
									alt="Brand"
								/>
								<img
									src={TextLogo}
									className="hidden-xs hidden-sm brand-img"
									alt="Brand"
								/>
								<Choose>
									<When condition={user.isCoordinator()}>
										<span className="persona visible-xs">
											{user.getTagName().substr(0, 5)}
										</span>
										<span className="persona hidden-xs">
											{user.getTagName()}
										</span>
									</When>
									<Otherwise>
										<span className="persona">{user.getTagName()}</span>
									</Otherwise>
								</Choose>
							</Link>
							<div className="nav navbar-brand pull-right hidden-sm hidden-md hidden-lg notification-btn">
								<button
									type="button"
									onClick={this.openNotificationsModal}
									className="btn btn-link"
									title="Notification History"
									label="Notification History"
								>
									<i className="rkf rkf-bell" />
								</button>
							</div>
						</div>
						{/* Navigation Links */}
						<div className="col-xs-12 col-sm-8">
							<div
								className="collapse navbar-collapse"
								id="navbar-links"
							>
								<ul className="nav navbar-nav">
									<For
										each="item"
										of={mainMenu}
										index="index"
									>
										<li
											key={`nav-link-${item.url.replace(/\//gi, "")}`}
											className={
												pathname.startsWith(item.url) ? "active" : ""
											}
										>
											<Link
												to={item.url}
												onClick={() => this.handleLinkClick(item.url)}
												data-test-id={item.dataTestId}
											>
												<i className={item.icon} />
												{item.text}
											</Link>
										</li>
									</For>
									{/* User menu options (displayed on mobile only). This section should match the dropdown section */}
									<li className="container-fluid visible-xs">
										<div className="row mobile-dropdown-header">
											<button
												type="button"
												className="btn col-xs-12 btn-link btn-block text-left"
												onClick={() => this.toggleMobileDropdown()}
											>
												<div className="row">
													<div className="col-xs-2">
														<Image
															fallbackSrc="avatar"
															src={user.Organization.OrganizationLogo}
															className="user-image"
														/>
													</div>
													<div className="col-xs-8">
														<h4>
															{user.FirstName} {user.LastName}
														</h4>
														<If
															condition={
																user.Organization &&
																user.IsStaffAdmin
															}
														>
															<h6>
																<em>{user.Organization.Name}</em>
															</h6>
														</If>
													</div>
													<div className="col-xs-2 text-right">
														<i
															className={`rkf rkf-caret-${isMobileDropdownOpen ? "up" : "down"}`}
														/>
													</div>
												</div>
											</button>
										</div>
										<ul
											className={classNames(
												"nav navbar-nav mobile-dropdown-items",
												{ open: isMobileDropdownOpen },
											)}
										>
											<For
												each="item"
												of={dropDown}
												index="index"
											>
												<li
													className={classNames("visible-xs", {
														active: pathname.startsWith(item.url),
													})}
													key={`user-menu-${item.url.replace(/\//gi, "")}`}
												>
													<Link
														to={item.url}
														onClick={() =>
															this.handleLinkClick(item.url)
														}
														data-test-id={item.dataTestId}
													>
														<i className={item.icon} />
														{item.text}
													</Link>
												</li>
											</For>
											<If condition={user.isSuperAdmin()}>
												<li className="visible-xs">
													<button
														type="button"
														className="btn btn-link user-menu-button"
														onClick={() =>
															this.handleLinkClick("impersonate")
														}
													>
														<i className="rkf rkf-user-secret" />
														{lang.t("Impersonate")}
													</button>
												</li>
											</If>
											<li
												role="separator"
												className="separator separator-solid visible-xs"
											/>
											<li className="visible-xs">
												<Link to="/logout">
													<i className="rkf rkf-exit" />
													{lang.t("Logout")}
												</Link>
											</li>
										</ul>
									</li>
									<li className="visible-xs">
										<a
											href={URLS.releaseNotesUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											<i className="rkf rkf-gift" />
											{lang.t("What_s New")}
										</a>
									</li>
									<li className="visible-xs">
										<button
											type="button"
											className="btn btn-link user-menu-button"
											onClick={() => this.handleLinkClick("help")}
										>
											<i className="rkf rkf-question-o" />
											{lang.t("Help")}
										</button>
									</li>
								</ul>
							</div>
						</div>
						{/* User Dropdown */}
						<div
							className="col-sm-2 hidden-xs"
							id="navbar-right-buttons"
						>
							<div className="nav navbar-brand notification-btn">
								<button
									aria-label="Notification History"
									type="button"
									onClick={this.openNotificationsModal}
									className="btn btn-link"
									title="Notification History"
								>
									<i className="rkf rkf-bell" />
								</button>
							</div>
							<ul className="nav navbar-nav navbar-right">
								<li
									className="dropdown"
									id="user-menu"
								>
									<button
										type="button"
										className="btn btn-link"
										onClick={() => this.toggleDropdown()}
									>
										<Image
											fallbackSrc="avatar"
											src={user.Organization.OrganizationLogo}
											className="user-image"
										/>
										<span className="user-name">
											{user.FirstName} {user.LastName}
										</span>
										<span className="caret" />
									</button>
									<ul className="dropdown-menu">
										<If condition={user.Organization && user.IsStaffAdmin}>
											<li
												role="presentation"
												className="organization-name"
											>
												{user.Organization.Name}
											</li>
											<li
												role="separator"
												className="separator separator-solid"
											/>
										</If>
										<For
											each="item"
											of={dropDown}
											index="index"
										>
											<li
												key={`user-dropdown-${item.url.replace(/\//gi, "")}`}
												className={
													pathname.startsWith(item.url) ? "active" : ""
												}
											>
												<Link
													to={item.url}
													onClick={() => this.handleLinkClick(item.url)}
													data-test-id={item.dataTestId}
												>
													<i className={item.icon} />
													{item.text}
												</Link>
											</li>
										</For>
										<If condition={user.isSuperAdmin()}>
											<li>
												<button
													type="button"
													className="btn btn-link btn-block text-left user-menu-button"
													onClick={() =>
														this.handleLinkClick("impersonate")
													}
												>
													<i className="rkf rkf-user-secret" />
													{lang.t("Impersonate")}
												</button>
											</li>
										</If>
										<li
											role="separator"
											className="separator separator-solid"
										/>
										<li>
											<a
												href={URLS.releaseNotesUrl}
												target="_blank"
												rel="noopener noreferrer"
											>
												<i className="rkf rkf-gift" />
												{lang.t("What_s New")}
											</a>
										</li>
										<li
											role="separator"
											className="separator separator-solid"
										/>
										<li>
											<Link to="/logout">
												<i className="rkf rkf-exit" />
												{lang.t("Logout")}
											</Link>
										</li>
									</ul>
								</li>
							</ul>
						</div>
					</div>
				</div>
				<ImpersonateModal
					show={impersonating}
					toggleModal={this.toggleImpersonation}
				/>
				<NotificationsModal
					ref={ref => {
						this._notificationsModal = ref;
					}}
				/>
			</nav>
		);
	}
}

const mapDispatchToProps = dispatch => ({
	resetPaymentFilters: bindActionCreators(PaymentActions, dispatch).resetFilters,
});

Navbar.propTypes = {
	user: PropTypes.object.isRequired,
	resetPaymentFilters: PropTypes.func,
};

Navbar.defaultProps = {
	resetPaymentFilters: () => {},
};

export default connect(null, mapDispatchToProps)(Navbar);
